import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const teeLinkVariants = cva(
  'inline-flex items-center justify-center gap-1.5 rounded-full cursor-pointer transition-all duration-200 focus-visible:outline-none disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary:
          'text-[var(--color-text-gold)] hover:underline disabled:text-neutral-500 var(--type-font-family-body)',
        secondary:
          'text-[hsl(var(--foreground))] underline decoration-1 underline-offset-2 hover:text-[hsl(var(--muted-foreground))] disabled:text-neutral-500 var(--type-font-family-body)',
        tertiary:
          'text-red-500 hover:text-red-600 disabled:text-red-300 var(--type-font-family-body)',
      },
      size: {
        s: 'text-body-xs font-regular',
        m: 'text-body-s font-medium',
        l: 'text-body-m font-semibold',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 's',
    },
  }
);

export interface TeeLinkProps
  extends
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'disabled'>,
    VariantProps<typeof teeLinkVariants> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
  /** Opens link in a new tab with rel="noopener noreferrer" */
  external?: boolean;
}

const TeeLink = React.forwardRef<HTMLAnchorElement, TeeLinkProps>(
  (
    {
      className,
      variant,
      size,
      leftIcon,
      rightIcon,
      children,
      style,
      disabled,
      external,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || props['aria-disabled'];

    return (
      <a
        ref={ref}
        className={cn(
          teeLinkVariants({ variant, size, className }),
          isDisabled && 'opacity-50 pointer-events-none'
        )}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...props}
      >
        {leftIcon && (
          <span className="flex shrink-0 items-center">{leftIcon}</span>
        )}
        {children}
        {rightIcon && (
          <span className="flex shrink-0 items-center">{rightIcon}</span>
        )}
      </a>
    );
  }
);

TeeLink.displayName = 'TeeLink';

export { TeeLink, teeLinkVariants };
